import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { scrollToId } from "@/components/site/SmoothScroll";
import { useCart } from "@/context/CartContext";
import { useSite } from "@/context/SiteContext";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { count, setOpen } = useCart();
  const { videos } = useSite();

  const NAV = [
    { label: "Music", type: "anchor", target: "music" },
    { label: "Upcoming", type: "anchor", target: "upcoming" },
    { label: "Platforms", type: "anchor", target: "platforms" },
    ...(videos && videos.length ? [{ label: "Watch", type: "anchor", target: "watch" }] : []),
    { label: "Merch", type: "route", target: "/merch" },
    { label: "About", type: "route", target: "/about" },
    { label: "Contact", type: "anchor", target: "connect" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (item) => {
    setMenuOpen(false);
    if (item.type === "route") navigate(item.target);
    else if (location.pathname === "/") scrollToId(item.target);
    else navigate("/", { state: { scrollTo: item.target } });
  };

  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50 transition-colors duration-500"
      style={{ backgroundColor: scrolled ? "var(--ink)" : "transparent", borderBottom: scrolled ? "var(--rule)" : "1px solid transparent" }}>
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8 flex items-center justify-between h-[110px]">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0 transition-opacity duration-500"
              style={{ opacity: location.pathname === "/" && !scrolled ? 0 : 1, pointerEvents: location.pathname === "/" && !scrolled ? "none" : "auto" }}
              aria-hidden={location.pathname === "/" && !scrolled}
              tabIndex={location.pathname === "/" && !scrolled ? -1 : 0}>
          <img src="/assets/img/logo.png" alt="Heart N Soul Records" className="h-20 sm:h-24 md:h-28 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-2 lg:gap-3" aria-label="Primary">
          {NAV.map((item) => (
            <button
              key={item.label}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => go(item)}
              className="font-caps nav-pill"
              style={{ fontSize: ".72rem", color: "var(--parchment)", background: "transparent",
                       border: "1px solid rgba(200,155,60,.28)", borderRadius: "999px",
                       padding: ".55rem 1.1rem", cursor: "pointer", fontWeight: 600,
                       letterSpacing: ".22em", transition: "all .25s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--gold)"; e.currentTarget.style.color = "var(--ink)"; e.currentTarget.style.borderColor = "var(--gold)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--parchment)"; e.currentTarget.style.borderColor = "rgba(200,155,60,.28)"; }}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button data-testid="cart-open-btn" onClick={() => setOpen(true)} className="relative text-parchment hover:text-gold transition-colors" aria-label="Open cart">
            <ShoppingBag size={22} />
            {count > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-2 h-4 min-w-4 px-1 rounded-full text-[10px] flex items-center justify-center font-caps" style={{ background: "var(--rose)", color: "var(--gold-lift)" }}>{count}</span>}
          </button>
          <button className="md:hidden text-parchment" onClick={() => setMenuOpen((v) => !v)} aria-label="Toggle menu" data-testid="mobile-menu-btn">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden px-6 pb-6 pt-2" style={{ backgroundColor: "var(--ink)", borderBottom: "var(--rule)" }} data-testid="mobile-menu">
          <div className="flex flex-col gap-5 pt-3">
            {NAV.map((item) => (
              <button key={item.label} onClick={() => go(item)} className="font-caps text-left" style={{ fontSize: ".85rem", color: "var(--parchment)", background: "none", border: "none" }} data-testid={`mnav-${item.label.toLowerCase()}`}>{item.label}</button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
